import { trackMailEvent } from "./mailAttribution.js";

const WCS_SCRIPT_ID = "naver-wcslog-script";
const DEFAULT_INFLOW_DOMAIN = "lawlow.info";

let loadPromise;

const getAccountId = () => process.env.REACT_APP_NAVER_CONVERSION_ACCOUNT_ID;

const getInflowDomain = () =>
  process.env.REACT_APP_NAVER_CONVERSION_INFLOW_DOMAIN || DEFAULT_INFLOW_DOMAIN;

const isTestMode = () =>
  ["true"].includes(
    String(process.env.REACT_APP_NAVER_CONVERSION_TEST_MODE).toLowerCase()
  );

const getConversionType = (type) => (isTestMode() ? `test_${type}` : type);

const canUseNaverConversion = () =>
  typeof window !== "undefined" && Boolean(getAccountId());

const configureNaverConversion = () => {
  if (!canUseNaverConversion()) return false;

  window.wcs_add = window.wcs_add || {};
  window.wcs_add.wa = getAccountId();

  if (window.wcs && typeof window.wcs.inflow === "function") {
    window.wcs.inflow(getInflowDomain());
  }

  return true;
};

export const initNaverConversion = () => {
  if (!canUseNaverConversion()) {
    return Promise.resolve(false);
  }

  configureNaverConversion();

  if (window.wcs && typeof window.wcs_do === "function") {
    return Promise.resolve(true);
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const existingScript = document.getElementById(WCS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        resolve(configureNaverConversion());
      });
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.id = WCS_SCRIPT_ID;
    script.async = true;
    script.src = "https://wcs.naver.net/wcslog.js";
    script.onload = () => {
      resolve(configureNaverConversion());
    };
    script.onerror = () => resolve(false);

    document.head.appendChild(script);
  });

  return loadPromise;
};

export const trackNaverPageView = async () => {
  const isReady = await initNaverConversion();

  if (!isReady || typeof window.wcs_do !== "function") return;

  window.wcs_do();
};

export const trackNaverConversion = async ({ type, value, id } = {}) => {
  if (!type) return;

  const isReady = await initNaverConversion();

  if (!isReady || !window.wcs || typeof window.wcs.trans !== "function") return;

  const conversion = {
    type: getConversionType(type),
  };

  if (value != null) {
    conversion.value = String(value);
  }

  if (id) {
    conversion.id = String(id);
  }

  window.wcs.trans(conversion);
};

export const trackNaverPurchase = ({ value, id } = {}) => {
  if (value == null) return;
  trackMailEvent("purchase", { value, id });
  return trackNaverConversion({ type: "purchase", value, id });
};

export const trackNaverBeginCheckout = ({ value, id } = {}) =>
  trackNaverConversion({ type: "begin_checkout", value, id });

export const trackNaverSignUp = ({ id } = {}) => {
  trackMailEvent("signup");
  return trackNaverConversion({ type: "sign_up", id });
};

export const trackNaverSearch = ({ id } = {}) =>
  trackNaverConversion({ type: "search", id });

export const trackNaverViewContent = ({ id } = {}) =>
  trackNaverConversion({ type: "view_content", id });

export const trackNaverLead = ({ id } = {}) =>
  trackNaverConversion({ type: "lead", id });

export const trackNaverShare = ({ id } = {}) =>
  trackNaverConversion({ type: "share", id });

export const trackNaverComparisonComplete = ({ id } = {}) =>
  trackNaverConversion({ type: "custom001", id });

export const trackNaverAppDownloadIntent = ({ id } = {}) =>
  trackNaverConversion({ type: "custom002", id });

export const trackNaverAppGuideView = ({ id } = {}) =>
  trackNaverConversion({ type: "custom003", id });
